<div class="md:p-2 min-h-screen">
    <!-- Header -->
    <div class="pb-4">
        <div class="flex items-center justify-between flex-wrap">
            <div>
                <h1 class="text-2xl font-bold text-gray-900">Territory Management</h1>
                <p class="text-gray-600 mt-1">Manage geographical territories and assign Field Service Representatives</p>
            </div>
            <div class="flex flex-row gap-2 mt-2 md:mt-0">
                <p-button 
                    icon="pi pi-expand" 
                    size="small" 
                    text 
                    (onClick)="showFullRegion()" 
                    *ngIf="selectedRegionObject" 
                    class="hidden md:block"
                    aria-label="Show full region area on map"
                    pTooltip="Show Full Region Area">
                </p-button>
                <p-button appGlobalLoadingDisable 
                    icon="pi pi-refresh" 
                    size="small" 
                    text 
                    (onClick)="resetRegionSelection()" 
                    class="hidden md:block"
                    aria-label="Reset region selection"
                    pTooltip="Change Region">
                </p-button>
                <!-- <p-button 
                    icon="pi pi-user-edit" 
                    size="small" 
                    text 
                    (onClick)="refreshUserAndReload()" 
                    *ngIf="isFSRUser()" 
                    class="hidden md:block"
                    aria-label="Refresh user data and reload territories"
                    pTooltip="Refresh User Data">
                </p-button> -->
                <p-select 
                    [options]="regions" 
                    [(ngModel)]="selectedRegionObject" 
                    optionLabel="name" 
                    placeholder="Search and choose region..." i18n-placeholder
                    styleClass="w-full" 
                    [style]="{width: '300px'}"
                    [filter]="true"
                    filterBy="name"
                    [showClear]="true"
                    (onChange)="onRegionSelect()"
                    [attr.aria-label]="'Search and select a region to view territories'">
                </p-select>
            </div>
        </div>
    </div>

    <!-- Main Content with Flex Layout -->
    <div class="flex flex-col 2xl:flex-row h-[calc(100vh-160px)]">
        <!-- Left Panel - Territories List -->
        <div class="w-full 2xl:w-96 bg-white rounded-xl mb-3 2xl:mb-0 2xl:mr-3 flex flex-col flex-shrink-0 overflow-y-auto">
            <div class="p-2 md:p-6 2xl:h-[calc(100vh-160px)]">
                <!-- Territories Header -->
                <div class="border-b border-gray-200 pb-4 mb-4">
                    <div class="flex justify-between items-center mb-4">
                        <div>
                            <h2 class="text-lg font-semibold text-gray-900">Territories</h2>
                            <p class="text-sm text-gray-600">{{ selectedRegionObject?.name }}</p>
                            <p class="text-xs text-blue-600 mt-1" *ngIf="isFSRUser()">
                                <i class="pi pi-info-circle mr-1"></i>Showing only your assigned territories
                            </p>
                        </div>
                        <div class="flex space-x-2" *ngIf="canCreateTerritory()">
                            <p-button 
                                icon="pi pi-plus" 
                                label="New Territory" 
                                severity="primary" 
                                size="small" 
                                routerLink="/admin/territory-management/add" 
                                [queryParams]="{region: selectedRegionObject?.name || ''}" 
                                [disabled]="loading"
                                aria-label="Create new territory in this region">
                            </p-button>
                        </div>
                    </div>

                    <!-- Search Bar -->
                    <div class="mb-4">
                        <label for="territory-search" class="sr-only">Search territories</label>
                        <p-inputGroup>
                            <p-inputGroupAddon>
                                <i class="pi pi-search" aria-hidden="true"></i>
                            </p-inputGroupAddon>
                            <input 
                                pInputText 
                                id="territory-search"
                                [(ngModel)]="searchQuery" 
                                (ngModelChange)="onSearchChange()" 
                                placeholder="Search territories..." i18n-placeholder
                                class="w-full"
                                aria-label="Search territories by name or other details">
                        </p-inputGroup>
                    </div>
                </div>

                <!-- Territories List -->
            <div class="hidden 2xl:block flex-1 overflow-y-auto space-y-3">
                @if(loading) {
                <div class="flex justify-center py-8" role="status" aria-live="polite">
                    <p-progressSpinner aria-label="Loading territories"></p-progressSpinner>
                </div>
                }

                @if(!loading && getFilteredTerritories().length === 0) {
                <div class="text-center py-8 text-gray-500" role="status">
                    <i class="pi pi-map text-4xl mb-2 block" aria-hidden="true"></i>
                    <p i18n>No territories found in this region</p>
                    <p class="text-sm mt-1" *ngIf="!isFSRUser() && canCreateTerritory()">Create a new territory to get started</p>
                    <p class="text-sm mt-1" *ngIf="!isFSRUser() && !canCreateTerritory()">No territories available in this region</p>
                    <p class="text-sm mt-1" *ngIf="isFSRUser()">You are not assigned to any territories in this region</p>
                </div>
                }

                @for (territory of getFilteredTerritories(); track $index) {
                <button 
                    type="button"
                    class="w-full text-left bg-white border rounded-lg p-4 pb-2 cursor-pointer hover:bg-gray-50 transition-all duration-200" 
                    [class.bg-blue-50]="selectedTerritory?._id === territory._id"
                    [class.border-blue-500]="selectedTerritory?._id === territory._id"
                    [class.border-2]="selectedTerritory?._id === territory._id"
                    [class.border-gray-200]="selectedTerritory?._id !== territory._id"
                    (click)="onTerritoryClick(territory)"
                    [attr.aria-label]="'Select territory ' + territory.name + ' in ' + territory.region"
                    [attr.aria-pressed]="selectedTerritory?._id === territory._id">

                    <div class="flex justify-between items-start mb-1">
                        <h3 class="font-semibold text-gray-900 text-lg">{{ territory.name }}</h3>
                        <p-tag [value]="territory.status" [severity]="getStatusSeverity(territory.status)" size="small">
                        </p-tag>
                    </div>

                    <p class="text-sm text-gray-600 mb-0">{{ territory.region }}</p>
                    @if (territory.refNo) {
                    <p class="text-xs text-gray-400 mt-1">{{ territory.refNo }}</p>
                    }

                    <div class="flex justify-between items-center">
                        <span class="text-sm text-gray-500 font-medium">{{ getAssignedUsersCount(territory) }} FSRs</span>
                        <div class="flex space-x-1" *ngIf="canEditTerritory()">
                            <p-button appGlobalLoadingDisable 
                                icon="pi pi-pencil" 
                                size="small" 
                                text 
                                severity="primary" 
                                (onClick)="editTerritory(territory); $event.stopPropagation()" 
                                [attr.aria-label]="'Edit territory ' + territory.name"
                                pTooltip="Edit Territory">
                            </p-button>
                        </div>
                    </div>
                </button>
                }
            </div>

            <div class="2xl:hidden mt-2">
                @if(loading) {
                <div class="flex justify-center py-12">
                    <div class="text-center">
                        <p-progressSpinner styleClass="w-8 h-8"></p-progressSpinner>
                        <p class="mt-2 text-sm text-gray-500">Loading territories...</p>
                    </div>
                </div>
                } @else {
                <p-carousel *ngIf="carouselKey >= 0" #territoryCarousel [value]="products" [numVisible]="3" [numScroll]="3" [circular]="false" [responsiveOptions]="responsiveOptions">
                    <ng-template let-product #item>
                        <div class="w-full md:w-64 bg-white border rounded-lg p-4 pb-2 cursor-pointer hover:bg-gray-50 transition-all duration-200" 
                             [class.bg-blue-50]="selectedTerritory?._id === product._id"
                             [class.border-blue-500]="selectedTerritory?._id === product._id"
                             [class.border-2]="selectedTerritory?._id === product._id"
                             [class.border-gray-200]="selectedTerritory?._id !== product._id"
                             (click)="onTerritoryClick(product)">
                            <div class="flex justify-between items-start mb-1">
                                <div class="flex flex-row items-center gap-3">
                                    @if(product._id === selectedTerritory?._id) {
                                    <div class="w-3 h-3 bg-blue-300 rounded-full "></div>
                                    }
                                    <h3 class="font-semibold text-gray-900 text-lg">{{ product.name }}</h3>
                                </div>

                                <p-tag [value]="product.status" [severity]="getStatusSeverity(product.status)" size="small">
                                </p-tag>
                            </div>

                            <p class="text-sm text-gray-600 mb-0">{{ product.region }}</p>

                            <div class="flex justify-between items-center">
                                <span class="text-sm text-gray-500 font-medium">{{ getAssignedUsersCount(product) }} FSRs</span>
                                <div class="flex space-x-1" *ngIf="canEditTerritory()">
                                    <p-button appGlobalLoadingDisable icon="pi pi-pencil" size="small" text severity="primary" (onClick)="editTerritory(product); $event.stopPropagation()" pTooltip="Edit Territory">
                                    </p-button>
                                </div>
                            </div>
                        </div>
                    </ng-template>
                </p-carousel>
                }
            </div>

            </div>
        </div>

        <!-- Right Panel - Map -->
        <div class=" right-panel flex-1 relative">
            <div class="w-full overflow-x-auto overflow-y-auto">
                <!-- Map Container -->
                <div #mapContainer class="map-container">
                    <google-map #mapRef [center]="center" [zoom]="zoom" [options]="options" class="min-h-screen">
                        <!-- Territory Polygons -->
                        <map-polygon *ngFor="let territory of territories" [paths]="territory.coordinates" [options]="{
                            fillColor: territory._id === selectedTerritory?._id ? '#10B981' : (territory.status === 'Active' ? '#3B82F6' : '#9CA3AF'),
                            fillOpacity: territory._id === selectedTerritory?._id ? 0.5 : 0.3,
                            strokeColor: territory._id === selectedTerritory?._id ? '#059669' : (territory.status === 'Active' ? '#1D4ED8' : '#6B7280'),
                            strokeWeight: territory._id === selectedTerritory?._id ? 3 : 2,
                            clickable: true
                        }" (mapClick)="onTerritoryClick(territory)">
                        </map-polygon>
                    </google-map>
                </div>
            </div>

            <!-- Territory Info Overlay -->
            <div *ngIf="selectedTerritory" class="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-xs z-10">
                <h4 class="font-medium text-gray-900 mb-2">{{ selectedTerritory.name }}</h4>
                <p class="text-sm text-gray-600 mb-2">{{ selectedTerritory.region }}</p>
                @if (selectedTerritory.refNo) {
                <p class="text-xs text-gray-400 mb-2">{{ selectedTerritory.refNo }}</p>
                }
                <p class="text-sm text-gray-500">{{ getAssignedUsersCount(selectedTerritory) }} FSRs assigned</p>
                <div class="mt-3" *ngIf="canEditTerritory()">
                    <p-button appGlobalLoadingDisable label="Edit Territory" icon="pi pi-pencil" size="small" severity="primary" (onClick)="editTerritory(selectedTerritory!)" class="w-full">
                    </p-button>
                </div>
            </div>

            
        </div>
    </div>

    <!-- Toast Messages -->
    <p-toast></p-toast>

    <!-- Confirmation Dialog -->
    <p-confirmDialog></p-confirmDialog>
</div>
